import { useEffect, useMemo, useRef, useState } from "react";
import {
  BriefcaseBusiness,
  CheckCircle2,
  CalendarDays,
  GripVertical,
  Plus,
  Search,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  FILE_BASE_URL,
  getEmployeesApi,
  getAdminUserTasksApi,
  getReportingStructureApi,
  saveReportingStructureApi,
} from "../../app/api";
import { toast } from "react-hot-toast";

const STORAGE_KEY = "uptaskReportingGroups";

const getNestedValue = (obj, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((current, part) => current?.[part], obj);
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

const normalizeFlag = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "active"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "inactive"].includes(normalized)) return false;
  return null;
};

const isCurrentEmployee = (employee = {}) => {
  const status = String(getNestedValue(employee, [
    "employment_status",
    "employee_status",
    "user_status",
    "status",
    "user.status",
    "employee.status",
  ])).trim().toLowerCase();

  const activeFlag = normalizeFlag(getNestedValue(employee, [
    "is_active",
    "active",
    "isActive",
    "user.is_active",
    "employee.is_active",
  ]));

  const deletedFlag = normalizeFlag(getNestedValue(employee, [
    "is_deleted",
    "deleted",
    "isDeleted",
    "user.is_deleted",
    "employee.is_deleted",
  ]));

  const exitDate = getNestedValue(employee, [
    "deleted_at",
    "exit_date",
    "left_at",
    "leaving_date",
    "last_working_day",
    "resigned_at",
    "terminated_at",
    "relieved_at",
    "user.deleted_at",
    "employee.deleted_at",
  ]);

  const inactiveStatuses = [
    "inactive",
    "deleted",
    "ex",
    "ex employee",
    "ex-employee",
    "former",
    "left",
    "resigned",
    "terminated",
    "relieved",
    "archived",
  ];

  if (activeFlag === false || deletedFlag === true || exitDate) return false;
  return !inactiveStatuses.includes(status);
};

const extractEmployeeList = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.users)) return response.data.users;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.users)) return response.users;
  return [];
};

const getEmployeeName = (employee = {}) =>
  String(
    employee.name ||
    employee.employee_name ||
    employee.full_name ||
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
    "Unknown"
  ).trim();

const getEmployeeId = (employee = {}) =>
  String(
    employee.id ||
    employee.user_id ||
    employee.employee_id ||
    employee.emp_id ||
    employee.roll_number ||
    employee.employee_code ||
    getEmployeeName(employee)
  );

const getEmployeeImage = (employee = {}) => {
  const path = getNestedValue(employee, [
    "profile_pic",
    "profilePic",
    "profile_image",
    "user.profile_pic",
    "employee.profile_pic",
  ]);
  if (!path) return "";
  const cleanPath = String(path).trim();
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return `${FILE_BASE_URL}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
};

const createGroup = (name = "New Reporting Group") => ({
  id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name,
  directorIds: [],
  managerId: "",
  employeeIds: [],
});

const getLocalGroups = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(saved) && saved.length ? saved : [createGroup("Leadership")];
  } catch {
    return [createGroup("Leadership")];
  }
};

const normalizeSavedGroups = (groups = [], employees = []) => {
  const validIds = new Set(employees.map(getEmployeeId));

  return groups
    .map((group) => ({
      id: String(group.id || createGroup().id),
      name: String(group.name || "Reporting Group").trim(),
      directorIds: Array.isArray(group.directorIds)
        ? [...new Set(group.directorIds.map(String).filter((id) => validIds.has(id)))]
        : [],
      managerId: validIds.has(String(group.managerId)) ? String(group.managerId) : "",
      employeeIds: Array.isArray(group.employeeIds)
        ? [...new Set(group.employeeIds.map(String).filter((id) => validIds.has(id)))]
        : [],
    }))
    .filter((group) => group.name);
};

const seedDirectorIds = (groups = [], employees = []) => {
  const directorNames = new Set(["harish mehra", "nitin jha"]);
  const directorIds = employees
    .filter((employee) => directorNames.has(getEmployeeName(employee).toLowerCase()))
    .map(getEmployeeId);

  if (!directorIds.length) return groups;

  return groups.map((group, index) => {
    if (group.directorIds?.length || index !== 0) return group;
    return {
      ...group,
      directorIds: directorIds.filter((id) => id !== group.managerId && !group.employeeIds.includes(id)),
      employeeIds: group.employeeIds.filter((id) => !directorIds.includes(id)),
      managerId: directorIds.includes(group.managerId) ? "" : group.managerId,
    };
  });
};

const todayInputValue = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const taskDateValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("T")[0].split(" ")[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatTaskDate = (value) => {
  const dateValue = taskDateValue(value);
  if (!dateValue) return "No date";
  const [year, month, day] = dateValue.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function EmployeeAvatar({ employee, size = "h-10 w-10" }) {
  const imageUrl = getEmployeeImage(employee);
  const name = getEmployeeName(employee);

  return (
    <div className={`${size} shrink-0 overflow-hidden rounded-full border border-white/10 bg-gradient-to-br from-violet-500 to-indigo-600`}>
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
      ) : (
        <div className="grid h-full w-full place-items-center text-sm font-black text-white">
          {name.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
}

function EmployeeChip({ employee, draggable = true, onDragStart, onRemove, onClick, compact = false }) {
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onClick}
      className={`group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-3 text-white shadow-lg shadow-black/10 transition hover:border-cyan-300/40 hover:bg-white/[0.09] ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <EmployeeAvatar employee={employee} size={compact ? "h-8 w-8" : "h-10 w-10"} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-black">{getEmployeeName(employee)}</p>
        <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-white/45">
          {employee.job_title || employee.role_name || `ID: ${getEmployeeId(employee)}`}
        </p>
      </div>
      {draggable && <GripVertical size={16} className="text-white/25" />}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="grid h-7 w-7 place-items-center rounded-lg text-white/45 transition hover:bg-rose-500/15 hover:text-rose-300"
          aria-label="Remove employee"
        >
          <X size={15} />
        </button>
      )}
    </div>
  );
}

const TASK_STATUS_STYLES = {
  TODO: "bg-blue-400/15 text-blue-100 border-blue-300/20",
  IN_PROGRESS: "bg-cyan-400/15 text-cyan-100 border-cyan-300/20",
  PENDING: "bg-amber-400/15 text-amber-100 border-amber-300/20",
  HOLD: "bg-rose-400/15 text-rose-100 border-rose-300/20",
  REVIEW: "bg-violet-400/15 text-violet-100 border-violet-300/20",
  DONE: "bg-emerald-400/15 text-emerald-100 border-emerald-300/20",
};

const normalizeTaskStatus = (status = "") =>
  String(status || "TODO").trim().toUpperCase().replace(/\s+/g, "_");

function OwnerTasksPanel({
  people,
  selectedEmployee,
  selectedUserId,
  date,
  tasks,
  allTasksCount,
  loading,
  onSelectUser,
  onDateChange,
}) {
  const statusCounts = tasks.reduce((counts, task) => {
    const status = normalizeTaskStatus(task.status);
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});

  return (
    <aside className="rounded-[24px] border border-white/10 bg-white/[0.05] p-4 shadow-2xl shadow-black/20 xl:sticky xl:top-4 xl:self-start">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.26em] text-cyan-200/75">
            Owner View
          </p>
          <h2 className="mt-1 text-xl font-black">Daily User Tasks</h2>
          <p className="mt-1 text-xs text-white/45">
            Owners and directors can review user tasks here.
          </p>
        </div>
        <CalendarDays className="shrink-0 text-cyan-300" size={24} />
      </div>

      <div className="mb-4 rounded-2xl border border-white/10 bg-black/20 p-3">
        <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-white/45">
          Select Person
        </label>
        <select
          value={selectedUserId || ""}
          onChange={(event) => onSelectUser(event.target.value)}
          className="h-11 w-full rounded-xl border border-white/10 bg-[#211638] px-3 text-sm font-bold text-white outline-none focus:border-cyan-300/50"
        >
          {people.map((employee) => (
            <option key={getEmployeeId(employee)} value={getEmployeeId(employee)}>
              {getEmployeeName(employee)}
            </option>
          ))}
        </select>

        <label className="mb-2 mt-3 block text-[10px] font-black uppercase tracking-widest text-white/45">
          Task Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(event) => onDateChange(event.target.value)}
          className="h-11 w-full rounded-xl border border-white/10 bg-[#211638] px-3 text-sm font-bold text-white outline-none focus:border-cyan-300/50"
        />
      </div>

      {selectedEmployee && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-cyan-300/15 bg-cyan-300/10 p-3">
          <EmployeeAvatar employee={selectedEmployee} size="h-12 w-12" />
          <div className="min-w-0">
            <p className="truncate text-base font-black">{getEmployeeName(selectedEmployee)}</p>
            <p className="truncate text-xs uppercase tracking-wider text-white/45">
              {selectedEmployee.job_title || selectedEmployee.role_name || `ID: ${getEmployeeId(selectedEmployee)}`}
            </p>
          </div>
        </div>
      )}

      <div className="mb-4 grid grid-cols-3 gap-2">
        <MiniTaskStat label="Daily" value={tasks.length} />
        <MiniTaskStat label="Total" value={allTasksCount} />
        <MiniTaskStat label="Done" value={statusCounts.DONE || 0} />
      </div>

      <div className="max-h-[calc(100vh-435px)] min-h-[360px] space-y-3 overflow-y-auto pr-1">
        {loading ? (
          <div className="grid min-h-[260px] place-items-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-300 border-t-transparent" />
          </div>
        ) : tasks.length ? (
          tasks.map((task) => <DailyTaskCard key={task.id} task={task} />)
        ) : (
          <div className="grid min-h-[260px] place-items-center rounded-2xl border border-dashed border-white/15 p-6 text-center">
            <div>
              <p className="text-sm font-bold text-white/75">No tasks for this date</p>
              <p className="mt-1 text-xs text-white/40">
                Try another date or select another employee.
              </p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function MiniTaskStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-3 text-center">
      <p className="text-lg font-black">{value}</p>
      <p className="text-[9px] font-black uppercase tracking-widest text-white/40">{label}</p>
    </div>
  );
}

function DailyTaskCard({ task }) {
  const status = normalizeTaskStatus(task.status);
  const statusClass = TASK_STATUS_STYLES[status] || TASK_STATUS_STYLES.TODO;

  return (
    <article className="rounded-2xl border border-white/10 bg-[#181126] p-4 shadow-lg shadow-black/15">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-black text-white">{task.title || "Untitled Task"}</h3>
          <p className="mt-1 text-[11px] text-white/42">
            Due {formatTaskDate(task.due_date)} | Created {formatTaskDate(task.created_at)}
          </p>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-wider ${statusClass}`}>
          {status.replace(/_/g, " ")}
        </span>
      </div>

      {task.description && (
        <p className="mb-3 line-clamp-3 text-xs leading-5 text-white/55">
          {task.description}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500"
            style={{ width: `${Math.max(0, Math.min(100, Number(task.completion_percent) || 0))}%` }}
          />
        </div>
        <span className="text-xs font-black text-white/70">
          {Number(task.completion_percent) || 0}%
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
        <span className="rounded-full bg-white/8 px-2 py-1 text-white/45">
          {task.priority || "Normal"}
        </span>
        {task.is_overdue && (
          <span className="rounded-full bg-rose-400/15 px-2 py-1 text-rose-100">
            Overdue
          </span>
        )}
        {task.points_earned !== undefined && task.points_earned !== null && (
          <span className="rounded-full bg-amber-400/15 px-2 py-1 text-amber-100">
            {task.points_earned} pts
          </span>
        )}
      </div>
    </article>
  );
}

export default function ReportingTool() {
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState(getLocalGroups);
  const [loading, setLoading] = useState(true);
  const [loadedStructure, setLoadedStructure] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [search, setSearch] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [dragOver, setDragOver] = useState("");
  const [selectedTaskUserId, setSelectedTaskUserId] = useState("");
  const [taskDate, setTaskDate] = useState(todayInputValue);
  const [userTasks, setUserTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const lastSavedPayload = useRef("");

  useEffect(() => {
    const loadReportingTool = async () => {
      try {
        setLoading(true);
        const [employeesRes, structureRes] = await Promise.all([
          getEmployeesApi({ page: 1, limit: 1000 }),
          getReportingStructureApi(),
        ]);

        if (!employeesRes.success && !Array.isArray(employeesRes.data)) {
          toast.error(employeesRes.message || "Failed to load employees");
          setEmployees([]);
          return;
        }

        const currentEmployees = extractEmployeeList(employeesRes)
          .filter(isCurrentEmployee)
          .sort((a, b) => getEmployeeName(a).localeCompare(getEmployeeName(b)));

        const backendGroups = Array.isArray(structureRes?.data?.groups)
          ? structureRes.data.groups
          : [];
        const sourceGroups = backendGroups.length ? backendGroups : getLocalGroups();
        const normalizedGroups = normalizeSavedGroups(sourceGroups, currentEmployees);
        const nextGroups = seedDirectorIds(normalizedGroups, currentEmployees);

        setEmployees(currentEmployees);
        setGroups(nextGroups.length ? nextGroups : [createGroup("Leadership")]);
        lastSavedPayload.current = backendGroups.length ? JSON.stringify(normalizedGroups) : "";
        if (!structureRes?.success) {
          setSaveStatus("offline");
        }
      } catch (error) {
        console.error("Failed to load reporting employees:", error);
        toast.error("Unable to load reporting tool");
        setSaveStatus("offline");
      } finally {
        setLoading(false);
        setLoadedStructure(true);
      }
    };

    loadReportingTool();
  }, []);

  useEffect(() => {
    if (selectedTaskUserId || !employees.length) return;

    const groupedIds = groups.flatMap((group) => [
      ...(group.directorIds || []),
      group.managerId,
      ...(group.employeeIds || []),
    ]).filter(Boolean);

    setSelectedTaskUserId(groupedIds[0] || getEmployeeId(employees[0]));
  }, [employees, groups, selectedTaskUserId]);

  useEffect(() => {
    if (!selectedTaskUserId) {
      setUserTasks([]);
      return;
    }

    let cancelled = false;
    const loadUserTasks = async () => {
      try {
        setTasksLoading(true);
        const res = await getAdminUserTasksApi(selectedTaskUserId);
        if (!cancelled) {
          if (res?.success) {
            setUserTasks(Array.isArray(res.data) ? res.data : []);
          } else {
            setUserTasks([]);
            toast.error(res?.message || "Failed to load user tasks");
          }
        }
      } catch {
        if (!cancelled) {
          setUserTasks([]);
          toast.error("Unable to load user tasks");
        }
      } finally {
        if (!cancelled) setTasksLoading(false);
      }
    };

    loadUserTasks();

    return () => {
      cancelled = true;
    };
  }, [selectedTaskUserId]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));

    if (!loadedStructure) return undefined;

    const payload = JSON.stringify(groups);
    if (payload === lastSavedPayload.current) return undefined;

    setSaveStatus("saving");
    const timer = setTimeout(async () => {
      const res = await saveReportingStructureApi({ groups });
      if (res?.success) {
        lastSavedPayload.current = payload;
        setSaveStatus("saved");
        return;
      }

      setSaveStatus("offline");
      toast.error(res?.message || "Reporting structure saved locally only");
    }, 600);

    return () => clearTimeout(timer);
  }, [groups, loadedStructure]);

  const employeeById = useMemo(() => {
    const map = new Map();
    employees.forEach((employee) => map.set(getEmployeeId(employee), employee));
    return map;
  }, [employees]);

  const filteredAvailableEmployees = employees.filter((employee) => {
    const text = `${getEmployeeName(employee)} ${employee.job_title || ""} ${employee.role_name || ""}`.toLowerCase();
    return text.includes(search.toLowerCase().trim());
  });

  const handleDrop = (event, groupId, slot) => {
    event.preventDefault();
    const employeeId = event.dataTransfer.getData("text/plain");
    if (!employeeId) return;

    setGroups((currentGroups) => {
      return currentGroups.map((group) => {
        if (group.id !== groupId) return group;
        if (slot === "director") {
          return {
            ...group,
            directorIds: group.directorIds?.includes(employeeId)
              ? group.directorIds
              : [...(group.directorIds || []), employeeId],
            managerId: group.managerId === employeeId ? "" : group.managerId,
            employeeIds: group.employeeIds.filter((id) => id !== employeeId),
          };
        }
        if (slot === "manager") {
          return {
            ...group,
            directorIds: (group.directorIds || []).filter((id) => id !== employeeId),
            managerId: employeeId,
            employeeIds: group.employeeIds.filter((id) => id !== employeeId),
          };
        }
        return {
          ...group,
          directorIds: (group.directorIds || []).filter((id) => id !== employeeId),
          employeeIds: group.employeeIds.includes(employeeId)
            ? group.employeeIds
            : [...group.employeeIds, employeeId],
        };
      });
    });

    setDragOver("");
  };

  const removeFromGroup = (groupId, employeeId, slot = "employees") => {
    setGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.id !== groupId) return group;
        if (slot === "director") {
          return {
            ...group,
            directorIds: (group.directorIds || []).filter((id) => id !== employeeId),
          };
        }
        if (slot === "manager") {
          return {
            ...group,
            managerId: group.managerId === employeeId ? "" : group.managerId,
          };
        }
        return {
          ...group,
          employeeIds: group.employeeIds.filter((id) => id !== employeeId),
        };
      })
    );
  };

  const handleAddGroup = () => {
    const name = newGroupName.trim();
    if (!name) {
      toast.error("Enter group name");
      return;
    }
    setGroups((current) => [...current, createGroup(name)]);
    setNewGroupName("");
  };

  const renameGroup = (groupId, name) => {
    setGroups((current) =>
      current.map((group) => (group.id === groupId ? { ...group, name } : group))
    );
  };

  const deleteGroup = (groupId) => {
    setGroups((current) => current.filter((group) => group.id !== groupId));
  };

  const totalManagers = groups.filter((group) => group.managerId).length;
  const totalDirectors = groups.reduce((sum, group) => sum + (group.directorIds?.length || 0), 0);
  const totalReportees = groups.reduce((sum, group) => sum + group.employeeIds.length, 0);
  const selectedTaskUser = employeeById.get(selectedTaskUserId);
  const visibleTaskPeople = useMemo(() => {
    const ids = groups.flatMap((group) => [
      ...(group.directorIds || []),
      group.managerId,
      ...(group.employeeIds || []),
    ]).filter(Boolean);
    const uniqueIds = [...new Set(ids)];
    const structuredPeople = uniqueIds.map((employeeId) => employeeById.get(employeeId)).filter(Boolean);
    return structuredPeople.length ? structuredPeople : employees;
  }, [employeeById, employees, groups]);
  const dailyTasks = userTasks.filter((task) => {
    const dueDate = taskDateValue(task.due_date);
    const createdDate = taskDateValue(task.created_at);
    return dueDate === taskDate || (!dueDate && createdDate === taskDate);
  });

  return (
    <div className="min-h-screen bg-[#12082F] p-4 text-white sm:p-6">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.28em] text-cyan-200">
              <BriefcaseBusiness size={14} />
              Employees
            </div>
            <h1 className="text-3xl font-black tracking-tight">Reporting Tool</h1>
            <p className="mt-1 text-sm text-white/55">
              Drag people into teams, assign managers, and shape reporting lines.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Groups" value={groups.length} />
            <Stat label="Directors" value={totalDirectors} />
            <Stat label="Managers" value={totalManagers} />
            <Stat label="Reportees" value={totalReportees} />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] border border-white/10 bg-white/[0.05] px-4 py-3 text-sm">
          <div>
            <p className="font-black text-white">Backend Save</p>
            <p className="text-xs text-white/45">Changes auto-save for every admin and HR user.</p>
          </div>
          <SaveStatus status={saveStatus} />
        </div>

        <div className="grid items-start gap-5 xl:grid-cols-[340px_minmax(520px,1fr)_420px]">
          <aside className="rounded-[24px] border border-white/10 bg-white/[0.05] p-4 shadow-2xl shadow-black/20 xl:sticky xl:top-4 xl:self-start">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-black">People Pool</h2>
                <p className="text-xs text-white/45">{filteredAvailableEmployees.length} available</p>
              </div>
              <UsersRound className="text-cyan-300" size={24} />
            </div>

            <div className="relative mb-4">
              <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search employees..."
                className="h-11 w-full rounded-2xl border border-white/10 bg-black/20 pl-10 pr-3 text-sm outline-none transition placeholder:text-white/35 focus:border-cyan-300/50"
              />
            </div>

            <div className="max-h-[calc(100vh-330px)] min-h-[360px] space-y-3 overflow-y-auto pr-1">
              {loading ? (
                <div className="grid min-h-[240px] place-items-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-300 border-t-transparent" />
                </div>
              ) : filteredAvailableEmployees.length ? (
                filteredAvailableEmployees.map((employee) => (
                  <EmployeeChip
                    key={getEmployeeId(employee)}
                    employee={employee}
                    onDragStart={(event) => event.dataTransfer.setData("text/plain", getEmployeeId(employee))}
                    onClick={() => setSelectedTaskUserId(getEmployeeId(employee))}
                  />
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-white/15 p-6 text-center text-sm text-white/45">
                  All matching employees are assigned.
                </div>
              )}
            </div>
          </aside>

          <section className="min-h-0 space-y-5 xl:max-h-[calc(100vh-205px)] xl:overflow-y-auto xl:pr-2">
            <div className="flex flex-col gap-3 rounded-[24px] border border-white/10 bg-[#1b1237]/95 p-4 shadow-2xl shadow-black/10 backdrop-blur sm:flex-row xl:sticky xl:top-0 xl:z-20">
              <input
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleAddGroup();
                }}
                placeholder="Add group name, e.g. Sales Team"
                className="h-12 min-w-0 flex-1 rounded-2xl border border-white/10 bg-black/20 px-4 text-sm outline-none transition placeholder:text-white/35 focus:border-violet-300/50"
              />
              <button
                type="button"
                onClick={handleAddGroup}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 to-violet-500 px-5 text-sm font-black text-white shadow-lg shadow-violet-500/20 transition hover:brightness-110"
              >
                <Plus size={18} />
                Add Group
              </button>
            </div>

            <div className="grid gap-5 2xl:grid-cols-2">
              {groups.map((group) => {
                const directors = (group.directorIds || []).map((id) => employeeById.get(id)).filter(Boolean);
                const manager = employeeById.get(group.managerId);
                const reportees = group.employeeIds.map((id) => employeeById.get(id)).filter(Boolean);

                return (
                  <div key={group.id} className="rounded-[26px] border border-white/10 bg-[#191032] p-4 shadow-2xl shadow-black/20">
                    <div className="mb-4 flex items-center gap-3">
                      <input
                        value={group.name}
                        onChange={(event) => renameGroup(group.id, event.target.value)}
                        className="min-w-0 flex-1 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-base font-black outline-none transition focus:border-violet-300/50"
                      />
                      <button
                        type="button"
                        onClick={() => deleteGroup(group.id)}
                        className="grid h-11 w-11 place-items-center rounded-2xl border border-rose-400/15 bg-rose-400/10 text-rose-300 transition hover:bg-rose-400/20"
                        title="Delete group"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>

                    <DropZone
                      title="Directors"
                      subtitle="Harish Mehra and Nitin Jha can sit here"
                      icon={<BriefcaseBusiness size={18} />}
                      active={dragOver === `${group.id}-director`}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDragOver(`${group.id}-director`);
                      }}
                      onDragLeave={() => setDragOver("")}
                      onDrop={(event) => handleDrop(event, group.id, "director")}
                    >
                      <div className="space-y-3">
                        {directors.length ? (
                          directors.map((employee) => (
                            <EmployeeChip
                              key={getEmployeeId(employee)}
                              employee={employee}
                              compact
                              onClick={() => setSelectedTaskUserId(getEmployeeId(employee))}
                              onDragStart={(event) => event.dataTransfer.setData("text/plain", getEmployeeId(employee))}
                              onRemove={() => removeFromGroup(group.id, getEmployeeId(employee), "director")}
                            />
                          ))
                        ) : (
                          <p className="rounded-2xl border border-dashed border-white/10 p-4 text-center text-xs text-white/35">
                            Drop directors here.
                          </p>
                        )}
                      </div>
                    </DropZone>

                    <DropZone
                      title="Manager"
                      subtitle="Drop one manager here"
                      icon={<UserRound size={18} />}
                      active={dragOver === `${group.id}-manager`}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDragOver(`${group.id}-manager`);
                      }}
                      onDragLeave={() => setDragOver("")}
                      onDrop={(event) => handleDrop(event, group.id, "manager")}
                    >
                      {manager ? (
                        <EmployeeChip
                          employee={manager}
                          compact
                          onClick={() => setSelectedTaskUserId(getEmployeeId(manager))}
                          onDragStart={(event) => event.dataTransfer.setData("text/plain", getEmployeeId(manager))}
                          onRemove={() => removeFromGroup(group.id, getEmployeeId(manager), "manager")}
                        />
                      ) : null}
                    </DropZone>

                    <DropZone
                      title="Employees"
                      subtitle="Drop reportees into this group"
                      icon={<UsersRound size={18} />}
                      active={dragOver === `${group.id}-employees`}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDragOver(`${group.id}-employees`);
                      }}
                      onDragLeave={() => setDragOver("")}
                      onDrop={(event) => handleDrop(event, group.id, "employees")}
                    >
                      <div className="space-y-3">
                        {reportees.length ? (
                          reportees.map((employee) => (
                            <EmployeeChip
                              key={getEmployeeId(employee)}
                              employee={employee}
                              compact
                              onClick={() => setSelectedTaskUserId(getEmployeeId(employee))}
                              onDragStart={(event) => event.dataTransfer.setData("text/plain", getEmployeeId(employee))}
                              onRemove={() => removeFromGroup(group.id, getEmployeeId(employee), "employees")}
                            />
                          ))
                        ) : (
                          <p className="rounded-2xl border border-dashed border-white/10 p-4 text-center text-xs text-white/35">
                            No employees assigned yet.
                          </p>
                        )}
                      </div>
                    </DropZone>
                  </div>
                );
              })}
            </div>
          </section>

          <OwnerTasksPanel
            people={visibleTaskPeople}
            selectedEmployee={selectedTaskUser}
            selectedUserId={selectedTaskUserId}
            date={taskDate}
            tasks={dailyTasks}
            allTasksCount={userTasks.length}
            loading={tasksLoading}
            onSelectUser={setSelectedTaskUserId}
            onDateChange={setTaskDate}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-right">
      <div className="text-2xl font-black text-white">{value}</div>
      <div className="text-[10px] font-black uppercase tracking-widest text-white/45">{label}</div>
    </div>
  );
}

function SaveStatus({ status }) {
  const labels = {
    idle: "Ready",
    saving: "Saving...",
    saved: "Saved to backend",
    offline: "Local draft",
  };

  const styles = {
    idle: "border-white/10 bg-white/[0.06] text-white/60",
    saving: "border-amber-300/25 bg-amber-300/10 text-amber-100",
    saved: "border-emerald-300/25 bg-emerald-300/10 text-emerald-100",
    offline: "border-rose-300/25 bg-rose-300/10 text-rose-100",
  };

  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-black uppercase tracking-widest ${styles[status] || styles.idle}`}>
      <span className={`h-2 w-2 rounded-full ${status === "saved" ? "bg-emerald-300" : status === "saving" ? "animate-pulse bg-amber-300" : status === "offline" ? "bg-rose-300" : "bg-white/35"}`} />
      {labels[status] || labels.idle}
    </div>
  );
}

function DropZone({ title, subtitle, icon, active, onDragOver, onDragLeave, onDrop, children }) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`mb-4 rounded-[22px] border p-4 transition ${
        active
          ? "border-cyan-300/70 bg-cyan-300/10 shadow-lg shadow-cyan-500/10"
          : "border-white/10 bg-white/[0.04]"
      }`}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-xl bg-white/10 text-cyan-200">{icon}</div>
          <div>
            <h3 className="text-sm font-black">{title}</h3>
            <p className="text-[11px] text-white/40">{subtitle}</p>
          </div>
        </div>
        <CheckCircle2 size={16} className={active ? "text-cyan-200" : "text-white/20"} />
      </div>
      {children}
    </div>
  );
}
